import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { format, subDays, subMonths } from 'date-fns';
import { Box, Button, ChipButton, KeyboardDatePicker, Popover, SegmentControl, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import styles from './TimePeriodSelector.module.scss';
export const GetTimePeriodSelectorTID = (parentTestId)=>getTestIds(parentTestId);
const formatDateShort = (timestamp)=>format(new Date(timestamp), 'MMM d, yyyy');
export const TimePeriodSelector = (param)=>{
    let { value, now, onChange, testId } = param;
    const { TimePeriodSelectorTestIds } = useMemo(()=>GetTimePeriodSelectorTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('components.invoiceMetricsSection');
    const { isMobileDevice } = useDevice();
    const chipRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState(value.preset === 'custom' ? value.fromDate : subMonths(now, 1).getTime());
    const [draftTo, setDraftTo] = useState(value.preset === 'custom' ? value.toDate : now.getTime());
    const segmentOptions = useMemo(()=>[
            {
                value: 'last-7-days',
                label: texts.timePeriodPresets.sevenDays(),
                testId: TimePeriodSelectorTestIds.Segment('last-7-days')
            },
            {
                value: 'last-1-month',
                label: texts.timePeriodPresets.thirtyDays(),
                testId: TimePeriodSelectorTestIds.Segment('last-1-month')
            },
            {
                value: 'last-3-months',
                label: texts.timePeriodPresets.lastThreeMonths(),
                testId: TimePeriodSelectorTestIds.Segment('last-3-months')
            },
            {
                value: 'all-time',
                label: texts.timePeriodPresets.allTime(),
                testId: TimePeriodSelectorTestIds.Segment('all-time')
            }
        ], [
        TimePeriodSelectorTestIds,
        texts
    ]);
    const segmentValue = value.preset === 'custom' ? '' : value.preset;
    const handleSegmentChange = (preset)=>{
        if (value.preset !== 'custom' && preset === value.preset) return;
        onChange({
            preset
        });
    };
    const openSpecificRange = ()=>{
        if (value.preset === 'custom') {
            setDraftFrom(value.fromDate);
            setDraftTo(value.toDate);
        } else {
            setDraftFrom(subMonths(now, 1).getTime());
            setDraftTo(now.getTime());
        }
        setPopoverOpen(true);
    };
    const applyCustomRange = ()=>{
        onChange({
            preset: 'custom',
            fromDate: draftFrom,
            toDate: draftTo
        });
        setPopoverOpen(false);
    };
    const specificRangeLabel = value.preset === 'custom' ? "".concat(formatDateShort(value.fromDate), " – ").concat(formatDateShort(value.toDate)) : texts.timePeriodPresets.specificRange();
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.root,
        testId: TimePeriodSelectorTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(SegmentControl, {
                testId: TimePeriodSelectorTestIds.SegmentControl(),
                className: styles.segmentControl,
                options: segmentOptions,
                value: segmentValue,
                onChange: handleSegmentChange,
                bi: {
                    name: 'time_period'
                }
            }),
            /*#__PURE__*/ _jsx(ChipButton, {
                ref: chipRef,
                testId: TimePeriodSelectorTestIds.SpecificRangeChip(),
                className: styles.specificRangeChip,
                highlight: value.preset === 'custom',
                onClick: openSpecificRange,
                children: specificRangeLabel
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: popoverOpen,
                anchorEl: chipRef.current,
                onClose: ()=>setPopoverOpen(false),
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                children: /*#__PURE__*/ _jsxs(Box, {
                    className: cn(styles.specificRangePopover, {
                        mobile: isMobileDevice
                    }),
                    children: [
                        /*#__PURE__*/ _jsxs(Box, {
                            className: styles.popoverInputs,
                            children: [
                                /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                                    bi: {
                                        name: 'metrics_from_date'
                                    },
                                    testId: TimePeriodSelectorTestIds.CustomFromPicker(),
                                    label: texts.timePeriodPresets.fromLabel(),
                                    value: new Date(draftFrom),
                                    onChange: (date)=>date != null && setDraftFrom(date.getTime()),
                                    maxDate: new Date(draftTo)
                                }),
                                /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                                    bi: {
                                        name: 'metrics_to_date'
                                    },
                                    testId: TimePeriodSelectorTestIds.CustomToPicker(),
                                    label: texts.timePeriodPresets.toLabel(),
                                    value: new Date(draftTo),
                                    onChange: (date)=>date != null && setDraftTo(date.getTime()),
                                    minDate: new Date(draftFrom)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'apply_custom_time_period'
                            },
                            testId: TimePeriodSelectorTestIds.ApplyButton(),
                            onClick: applyCustomRange,
                            className: styles.applyButton,
                            children: texts.timePeriodPresets.applyButton()
                        })
                    ]
                })
            })
        ]
    });
};
export function getDateRangeForTimePeriod(value, now) {
    switch(value.preset){
        case 'last-7-days':
            return {
                issuedOnSince: subDays(now, 7).getTime(),
                issuedOnUntil: now.getTime()
            };
        case 'last-1-month':
            return {
                issuedOnSince: subMonths(now, 1).getTime(),
                issuedOnUntil: now.getTime()
            };
        case 'last-3-months':
            return {
                issuedOnSince: subMonths(now, 3).getTime(),
                issuedOnUntil: now.getTime()
            };
        case 'all-time':
            return {
                issuedOnSince: null,
                issuedOnUntil: null
            };
        case 'custom':
            return {
                issuedOnSince: value.fromDate,
                issuedOnUntil: value.toDate
            };
    }
}
